class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('formdata', this.onFormData.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  getExpandedQuery(query = this.getQuery()) {
    return window.BeetlesSearchQuery.storefrontQuery(query);
  }
  getEffectiveQuery() {
    return this.resolvedBaseQuery === this.getQuery() ? this.resolvedQuery : this.getQuery();
  }
  onFormData(event) {
    event.formData.set('q', this.getExpandedQuery(this.getEffectiveQuery()));
    event.formData.set('base_q', this.getQuery());
    event.formData.set('options[prefix]', window.BeetlesSearchQuery.skuQuery(this.getQuery()) ? 'none' : 'last');
  }
  clear(event = null) {
    event && event.preventDefault();
    this.activeSearchRequest = null;
    this.resolvedBaseQuery = null;
    this.removeAttribute('loading');
    this.resetButton.classList.remove('btn--loading');
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.removeAttribute('has-query');
  }

  setupURL() {
    // Predictive search does not support field syntax; use storefront SKU results.
    if (window.BeetlesSearchQuery.skuQuery(this.getQuery())) return this.setupBestSellingSearchURL();
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    const useBestSellingProducts = this.shouldUseBestSellingProducts();

    return (
      url.searchParams.set('q', this.getExpandedQuery()),
      url.searchParams.set('base_q', this.getQuery()),
      url.searchParams.set('resources[type]', useBestSellingProducts ? 'query,product' : 'query,product,collection,article,page'),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('resources[options][unavailable_products]', 'last'),
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku'),
      useBestSellingProducts && url.searchParams.set('sort_by', 'best-selling'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.searchContent?.classList.remove('hidden');
    this.setAttribute('has-query', '');
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  async fetchSearchResponse(url) {
    if (this.cachedMap.has(url)) return this.cachedMap.get(url);
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok');
    const responseText = await response.text();
    this.cachedMap.set(url, responseText);
    return responseText;
  }

  async renderSection(url) {
    const query = this.getQuery();
    const request = {};
    this.activeSearchRequest = request;
    const isCurrent = () => this.activeSearchRequest === request && this.getQuery() === query;
    this.resolvedBaseQuery = null;
    this.setAttribute('loading', 'true');
    this.removeAttribute('error');
    this.resetButton.classList.add('btn--loading');

    try {
      const responseText = await this.fetchSearchResponse(url);
      if (!isCurrent()) return;
      this.resolvedBaseQuery = query;
      this.resolvedQuery = query;
      const rendered = this.renderSearchResults(responseText);
      this.toggleAttribute('results', rendered);
      if (!rendered) this.showFallbackContent();
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error fetching data: ', error);
      this.setAttribute('error', 'Failed to load data');
      this.showFallbackContent();
    } finally {
      if (this.activeSearchRequest === request) {
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
      }
    }
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      if (window.BeetlesSearchQuery.skuQuery(this.getQuery())) {
        const cards = Array.from(parsedDoc.querySelectorAll('#ProductsList .product-card-simple'))
          .slice(0, parseInt(this.dataset.resultsLimit || '6', 10));
        if (!cards.length) {
          // A SKU miss must not fall back to unrelated recommendations.
          targetElement.innerHTML = '';
          this.showFallbackContent(true);
          return true;
        }
        const wrapper = document.createElement('div');
        wrapper.className = 'grid gap-3 predictive-search-result search__box-item predictive-search-result--products';
        const heading = document.createElement('h4');
        heading.className = 'h5 predictive-search-result__heading';
        heading.textContent = this.dataset.productsHeading || 'Products';
        const list = document.createElement('ul');
        list.id = 'predictive-search-results-products-list';
        list.className = 'grid gap-4';
        list.setAttribute('role', 'group');
        cards.forEach((card, index) => {
          this.normalizePredictiveProductCard(card);
          const item = document.createElement('li');
          item.id = `predictive-search-option-product-${index + 1}`;
          item.className = 'predictive-search__list-item';
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', 'false');
          item.append(card);
          list.append(item);
        });
        wrapper.append(heading, list);
        const footer = document.createElement('div');
        footer.className = 'search__results-all block';
        const viewAll = document.createElement('button');
        viewAll.type = 'submit';
        viewAll.className = 'reversed-link btn--inherit flex items-center justify-center gap-2 font-body-bolder';
        viewAll.textContent = 'VIEW ALL ITEMS →';
        footer.append(viewAll);
        targetElement.replaceChildren(wrapper, footer);
        this.searchContent?.classList.remove('hidden');
        return true;
      }
      const contentElement =
        parsedDoc.getElementById(id) ||
        parsedDoc.querySelector('[id^="PredictiveSearchResults-"]') ||
        parsedDoc.querySelector('.search__results [role="listbox"]');

      if (contentElement) {
        if (!contentElement.innerHTML.trim()) {
          return false;
        }
        this.restoreDisplayQuery(contentElement);
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
        this.renderBestSellingProducts();
        return true;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
    return false;
  }
  restoreDisplayQuery(contentElement) {
    const heading = contentElement.querySelector('.empty-state h4');
    if (!heading) return;

    heading.textContent = heading.textContent
      .replace(this.getExpandedQuery(this.getEffectiveQuery()), this.getQuery())
      .replace(this.getEffectiveQuery(), this.getQuery());
  }
  setupBestSellingSearchURL() {
    const searchUrl = FoxTheme.routes.search_url || '/search';
    const url = new URL(searchUrl, window.location.origin);
    const query = this.getQuery();

    url.searchParams.set('q', this.getExpandedQuery(this.getEffectiveQuery()));
    url.searchParams.set('base_q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', window.BeetlesSearchQuery.skuQuery(query) ? 'none' : 'last');
    url.searchParams.set('options[fields]', 'title,product_type,variants.title,vendor,variants.sku');
    url.searchParams.set('sort_by', 'best-selling');
    // JSON template section IDs vary by theme; fetch the complete page for SKUs.
    if (!window.BeetlesSearchQuery.skuQuery(query)) url.searchParams.set('section_id', 'main');

    return url;
  }
  shouldUseBestSellingProducts() {
    return Boolean(this.closest('.search-drawer') || this.closest('.template-search'));
  }
  renderBestSellingProducts() {
    if (!this.shouldUseBestSellingProducts()) return;

    const currentQuery = this.getQuery();
    const resultsList = this.querySelector('#predictive-search-results-products-list');
    if (!currentQuery || !resultsList) return;

    const url = this.setupBestSellingSearchURL().toString();
    const limit = parseInt(this.dataset.resultsLimit || '6', 10);

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Best-selling search response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        if (this.getQuery() !== currentQuery) return;

        const parser = new DOMParser();
        const parsedDoc = parser.parseFromString(responseText, 'text/html');
        const productCards = Array.from(parsedDoc.querySelectorAll('#ProductsList .product-card-simple')).slice(0, limit);
        if (!productCards.length) return;

        resultsList.innerHTML = productCards
          .map((card, index) => {
            this.normalizePredictiveProductCard(card);
            return `
              <li
                id="predictive-search-option-product-${index + 1}"
                class="predictive-search__list-item"
                role="option"
                aria-selected="false"
              >
                ${card.outerHTML}
              </li>
            `;
          })
          .join('');
      })
      .catch((error) => {
        console.error('Error fetching best-selling search products: ', error);
      });
  }
  createProductCardActionLink(productUrl, sourceButton, isSmall = false) {
    const link = document.createElement('a');
    const label = sourceButton?.textContent?.trim() || 'ADD TO BAG';

    link.href = productUrl || '#';
    link.className = `product-card-simple__btn${isSmall ? ' product-card-simple__btn--small' : ''}`;
    if (sourceButton?.disabled || sourceButton?.classList.contains('product-card-simple__btn--disabled')) {
      link.classList.add('product-card-simple__btn--disabled');
      link.setAttribute('aria-disabled', 'true');
    }
    link.innerHTML = `<span>${label}</span>`;

    return link;
  }
  normalizePredictiveProductCard(card) {
    card.classList.add('search__product-card');

    const productUrl =
      card.querySelector('.product-card-simple__title a')?.getAttribute('href') ||
      card.querySelector('.product-card-simple__image-wrapper a')?.getAttribute('href') ||
      '#';

    card.querySelectorAll('.product-card-simple__actions-desktop form').forEach((form) => {
      form.replaceWith(this.createProductCardActionLink(productUrl, form.querySelector('.product-card-simple__btn')));
    });

    card.querySelectorAll('.product-card-simple__footer-mobile form').forEach((form) => {
      form.replaceWith(this.createProductCardActionLink(productUrl, form.querySelector('.product-card-simple__btn'), true));
    });
  }
  showFallbackContent(forceEmpty = false) {
    this.searchContent?.classList.remove('hidden');

    const recommendations = this.querySelector('.search__recommendations');
    if (!forceEmpty && recommendations && recommendations.innerHTML.trim().length > 0) {
      this.removeAttribute('results');
      return;
    }

    const results = this.querySelector('[id^="PredictiveSearchResults-"]');
    if (!results) return;

    results.innerHTML = `
      <div class="empty-state flex flex-col gap-3 items-center justify-center text-center w-full h-full">
        <h4>No results found for "${this.getQuery().replace(/[&<>"']/g, (char) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        })[char])}"</h4>
        <p class="m-0">Try a different keyword or view all search results.</p>
      </div>
    `;
    this.setAttribute('results', '');
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
    this.input?.classList.add('snize-exclude-input');
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
  show(focusElement = this.focusElement, animate = true) {
    this.querySelectorAll('.snize-ac-results, .snize-ac-results-mobile, .snize-ac-results-content').forEach((element) =>
      element.remove()
    );
    super.show(focusElement, animate);
  }
}
customElements.define('search-drawer', SearchDrawer);
